import * as React from 'react';
import { cloneDeep } from 'lodash';
import { useSelector } from 'react-redux';
import { IReduxState } from 'src/reduxTypes';
import { toastr } from 'react-redux-toastr';
import { NewsService } from 'src/share/services/news';
import SidebarContainer from 'src/modules/Routing/Sidebar/SidebarContainer';
import NewsHeader from './components/NewsHeader';
import NewsArticles from './components/NewsArticles';
import { INewsState, initialNewsState } from 'src/share/types/news';
import ConfirmationModal from 'src/share/components/ConfirmationModal/ConfirmationModal';
import useInfiniteScroll from 'src/share/hooks/useInfiniteScroll';
import { RoutesModules } from 'src/modules/Routing/types';
import useLangFilter from './hooks/useLangFilter';
import useDateRangeFilter, { DateRangeOptions } from '../../share/hooks/useDateRangeFilter';
import useDidUpdateEffect from 'src/share/hooks/useDidUpdateEffect';
import './News.css';

const News = () => {
  const agencyCode = useSelector((reduxState: IReduxState) => reduxState.auth.agencyCode);
  const observer = React.useRef();
  const [state, setState] = React.useState<INewsState>(cloneDeep(initialNewsState));
  const [dateRange, setDateRange] = useDateRangeFilter();
  const [lang, setLang] = useLangFilter("en", resetNews);
  const { handleLastImageRef } = useInfiniteScroll(observer, state.hasMore, state.isLoading, onImageRef);
  React.useEffect(() => {
    if (state.isLoading) {
      fetchNews();
    }
  }, [state.isLoading]);
  useDidUpdateEffect(() => {
    if (dateRange.startDate && dateRange.endDate && !dateRange.focus) {
      resetNews();
    }
  }, [dateRange.focus]);
  async function fetchNews() {
    try {
      const { page, size } = state;
      const { startDate, endDate } = dateRange;
      const range = startDate && endDate && { gte: startDate.valueOf(), lte: endDate.valueOf() };
      const { news, total } = await NewsService.get({ page, size, dateRange: range, lang });
      setState(prevState => {
        const fetchedNews = [...prevState.news, ...news];
        return { ...prevState, news: fetchedNews, total, isLoading: false, hasMore: fetchedNews.length < total };
      });
    } catch (error) {
      toastr.error('Error', error);
      setState(prevState => ({ ...prevState, isLoading: false }));
    }
  }
  function onImageRef() {
    setState(prevState => ({ ...prevState, page: prevState.page + 1, isLoading: true }));
  }
  function resetNews() {
    setState(prevState => ({ ...prevState, news: [], page: 0, total: 0, isLoading: true }))
    document.getElementsByClassName('scrollbar-wrapper')[0].scrollTop = 0;
  }
  const handleRemove = async () => {
    try {
      setState(prevState => ({ ...prevState, isDeleting: true }));
      await NewsService.delete(state.selectedArticle);
      const news = state.news.filter(article => article.id !== state.selectedArticle);
      setState((prevState) => ({ ...prevState, news, total: prevState.total - 1, selectedArticle: "", isDeleting: false, toggleModal: false }));
      toastr.success("Success", "The article was removed from the list.");
    } catch (error) {
      toastr.error("Error", error);
    }
  }
  return (
    <div className="news-container" id="news-container">
      <NewsHeader
        dateRange={dateRange}
        total={state.total}
        onDatesChange={(date) => setDateRange({ type: DateRangeOptions.SET_DATES, payload: date })}
        onDateFocusChange={({ focus }) => setDateRange({ type: DateRangeOptions.SET_FOCUS, payload: focus })}
        onLangChange={(selectedLang) => setLang(selectedLang)}
      />
      <NewsArticles
        news={state.news}
        refElement={handleLastImageRef}
        isAdmin={agencyCode === "ALL"}
        isLoading={state.isLoading}
        onRemove={(newsId) => setState(prevState => ({ ...prevState, selectedArticle: newsId, toggleModal: true }))}
      />
      <ConfirmationModal
        isOpen={state.toggleModal}
        toggle={() => setState(prevState => ({ ...prevState, toggleModal: !state.toggleModal }))}
        title={'Discard News Article'}
        description={state.isDeleting ? 'Deleting...' : `Do you want to remove the selected article?`}
        onContinue={handleRemove}
      />
    </div>
  );
}

export default SidebarContainer(RoutesModules.NEWS)(News);


// WEBPACK FOOTER //
// ./src/modules/News/News.tsx